import moment from 'moment';

import { SortType } from '../../../ColumnHeader';
import { getEventRedirectUrl, ImportedEventSources } from '../../../../utils/events';
import { getPlayerId, getPlayerName } from '../../playerShape';
import {
    SavedListOrderByRule,
    MatchType,
    EventActivitySource,
    EventActivityStatus,
    EVENT_STATUS_PRIORITY,
    EVENT_STATUS_LABELS,
    MAX_ACTIVITY_ROWS,
    RESULT_DATE_FORMAT,
    EVENT_DATE_FORMAT,
    OPPONENT_PROFILE_PATH,
    TMS_EVENT_SOURCE,
} from './constants';

export const sortTypeToOrderByRule = sortType => (
    sortType === SortType.ASC ? SavedListOrderByRule.ASCENDING : SavedListOrderByRule.DESCENDING
);

// Uppercase initials for the avatar fallback (no profile image): first + last initial when the feed
// sends both, otherwise the first letters of the first two words of the display name.
export const getInitials = ({ firstName, lastName, displayName } = {}) => {
    if (firstName || lastName) {
        return `${(firstName || '').charAt(0)}${(lastName || '').charAt(0)}`.toUpperCase();
    }
    return (displayName || '')
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map(word => word.charAt(0))
        .join('')
        .toUpperCase();
};

const readLocation = (location) => {
    if (typeof location === 'string') {
        return location;
    }
    return location?.display ?? '';
};

// The feed player payload is loosely typed (see playerShape) — read identity through the shared
// accessors and carry the FLAT rating fields (TMS-2280) straight through: the same field set
// importedEventPlayerShape carries, so setRatingChoice / Utr / UtrLabel resolve them for the shared
// Rating exactly as they do for the 3rd-Party/CPP rows. The weekly delta rides here too and RatingCell
// gates it by the resolved fully-reliable state.
export const normalizePlayer = (item = {}) => ({
    id: getPlayerId(item),
    displayName: getPlayerName(item) || [item.firstName, item.lastName].filter(Boolean).join(' '),
    firstName: item.firstName ?? '',
    lastName: item.lastName ?? '',
    gender: item.gender ?? '',
    location: readLocation(item.location),
    playerProfileImages: item.playerProfileImages ?? item.playerImages ?? null,
    recentActivity: !!item.recentActivity,
    singlesUtr: item.singlesUtr ?? null,
    singlesUtrDisplay: item.singlesUtrDisplay ?? null,
    ratingStatusSingles: item.ratingStatusSingles ?? null,
    ratingProgressSingles: item.ratingProgressSingles ?? null,
    doublesUtr: item.doublesUtr ?? null,
    doublesUtrDisplay: item.doublesUtrDisplay ?? null,
    ratingStatusDoubles: item.ratingStatusDoubles ?? null,
    ratingProgressDoubles: item.ratingProgressDoubles ?? null,
    myUtrSingles: item.myUtrSingles ?? null,
    myUtrSinglesDisplay: item.myUtrSinglesDisplay ?? null,
    myUtrStatusSingles: item.myUtrStatusSingles ?? null,
    myUtrProgressSingles: item.myUtrProgressSingles ?? null,
    myUtrDoubles: item.myUtrDoubles ?? null,
    myUtrDoublesDisplay: item.myUtrDoublesDisplay ?? null,
    myUtrStatusDoubles: item.myUtrStatusDoubles ?? null,
    myUtrProgressDoubles: item.myUtrProgressDoubles ?? null,
    utrRange: item.utrRange ?? null,
    historicRatings: item.historicRatings ?? null,
    singlesDelta: item.singlesDelta ?? null,
    doublesDelta: item.doublesDelta ?? null,
    // Per-player note rides the feed item (TMS-2174/2178); its presence drives the row note indicator.
    // Dual-name fallback like the rating/image fields above: the value persists on SavedPlayer.Notes, so
    // accept either the singular `note` or a plural `notes` serialization.
    note: item.note ?? item.notes ?? '',
    // Recent Results + Event Activity arrays (TMS-2183). Carried through raw and shaped for display by
    // the cells; an absent array becomes an empty one so the cell renders the `—` placeholder.
    recentResults: Array.isArray(item.recentResults) ? item.recentResults : [],
    eventActivity: Array.isArray(item.eventActivity) ? item.eventActivity : [],
});

const isDoublesMatch = matchType => String(matchType ?? '').toLowerCase() === MatchType.DOUBLES.toLowerCase();

// W/L is loosely serialized — accept an explicit boolean, a 'W'/'L' code, or a 'Win'/'Loss' word.
const resolveWin = (result) => {
    if (typeof result.isWin === 'boolean') {
        return result.isWin;
    }
    if (typeof result.win === 'boolean') {
        return result.win;
    }
    const outcome = String(result.outcome ?? result.result ?? '').trim().toLowerCase();
    return outcome === 'w' || outcome === 'win' || outcome === 'won';
};

const formatOpponentName = (opponent = {}) => {
    const first = opponent.firstName ?? '';
    const last = opponent.lastName ?? '';
    if (first && last) {
        return `${first.charAt(0)}. ${last}`;
    }
    return getPlayerName(opponent) || last || first;
};

// The opponent UTR shown is the rating matching the result's match type. The feed sends both ratings
// per opponent; unwrap the common numeric / string / { rating } shapes and render it verbatim.
const formatOpponentRating = (opponent, doubles) => {
    const raw = doubles ? opponent.doublesRating : opponent.singlesRating;
    if (raw === null || raw === undefined || raw === '') {
        return '';
    }
    const value = typeof raw === 'object' ? (raw.ratingDisplay ?? raw.display ?? raw.rating ?? '') : raw;
    return value === null || value === undefined ? '' : String(value);
};

// Format in UTC: the feed sends a UTC instant, so a local parse would render the day before west of
// UTC (e.g. a midnight-UTC date showing as the prior evening).
export const formatResultDate = (date) => {
    if (!date) {
        return '';
    }
    const parsed = moment.utc(date);
    return parsed.isValid() ? parsed.format(RESULT_DATE_FORMAT) : '';
};

export const formatEventDate = (date) => {
    if (!date) {
        return '';
    }
    const parsed = moment.utc(date);
    return parsed.isValid() ? parsed.format(EVENT_DATE_FORMAT) : '';
};

// Shape a raw feed result into what RecentResultsCell renders: the doubles flag, W/L, date, score, and
// each opponent with the profile path + the match-type-appropriate UTR.
export const shapeResult = (result = {}) => {
    const doubles = isDoublesMatch(result.matchType);
    const opponents = Array.isArray(result.opponents) ? result.opponents : [];
    return {
        isDoubles: doubles,
        isWin: resolveWin(result),
        date: formatResultDate(result.date),
        score: result.score ?? '',
        opponents: opponents.map((opponent) => {
            const playerId = getPlayerId(opponent);
            return {
                playerId,
                name: formatOpponentName(opponent),
                rating: formatOpponentRating(opponent, doubles),
                // Guard the interpolation: a missing playerId must not render a live /profiles/undefined
                // link — the cell falls back to plain text when this is null.
                profileUrl: playerId === null || playerId === undefined ? null : `${OPPONENT_PROFILE_PATH}/${playerId}`,
            };
        }),
    };
};

const normalizeStatus = (status) => {
    const value = String(status ?? '').replace(/\s+/g, '').toLowerCase();
    if (value === EventActivityStatus.IN_PROGRESS.toLowerCase()) {
        return EventActivityStatus.IN_PROGRESS;
    }
    if (value === EventActivityStatus.UPCOMING.toLowerCase()) {
        return EventActivityStatus.UPCOMING;
    }
    if (value === EventActivityStatus.COMPLETED.toLowerCase()) {
        return EventActivityStatus.COMPLETED;
    }
    return null;
};

// Compose an event link from the feed's source + eventId (the feed sends no URL) by delegating to the
// app's canonical getEventRedirectUrl — Leagues maps to the imported-league route (which validates the
// id), everything else (Tms) to the TMS event profile. A missing/invalid id yields null (not a dead
// `/events/undefined` link) so the cell renders the title as plain text instead.
export const eventActivityUrl = (source, eventId) => {
    if (eventId === null || eventId === undefined || eventId === '') {
        return null;
    }
    const mappedSource = source === EventActivitySource.LEAGUES ? ImportedEventSources.LEAGUE : TMS_EVENT_SOURCE;
    return getEventRedirectUrl(eventId, mappedSource) || null;
};

// Shape a raw feed event into what EventActivityCell renders: the resolved status + its label, the date
// (kept only for Upcoming), the title, and the composed link.
export const shapeEvent = (event = {}) => {
    const status = normalizeStatus(event.status);
    const isUpcoming = status === EventActivityStatus.UPCOMING;
    return {
        eventId: event.eventId,
        status,
        label: status ? EVENT_STATUS_LABELS[status] : '',
        date: isUpcoming ? formatEventDate(event.date) : '',
        title: event.eventName ?? event.title ?? event.name ?? '',
        url: eventActivityUrl(event.source, event.eventId),
    };
};

// Cap at two events, ordered in-progress → upcoming → completed; unknown statuses sort last but keep
// their feed order (a stable sort).
export const orderEventActivity = (events = []) => (
    [...events]
        .sort((a, b) => {
            const aRank = EVENT_STATUS_PRIORITY[normalizeStatus(a.status)] ?? Number.MAX_SAFE_INTEGER;
            const bRank = EVENT_STATUS_PRIORITY[normalizeStatus(b.status)] ?? Number.MAX_SAFE_INTEGER;
            return aRank - bRank;
        })
        .slice(0, MAX_ACTIVITY_ROWS)
);

// At most one Singles + one Doubles (the feed already sends this shape; enforced client-side per the
// ticket). Keeps the first of each match type, singles before doubles, matching the design order.
export const capResults = (results = []) => {
    const singles = results.find(result => !isDoublesMatch(result.matchType));
    const doubles = results.find(result => isDoublesMatch(result.matchType));
    return [singles, doubles].filter(Boolean).slice(0, MAX_ACTIVITY_ROWS);
};
